import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, Clock, MapPin, User, CheckCircle2, MessageSquare, Loader2
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const statusColor = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-green-50 text-green-700 border-green-200",
  completed: "bg-slate-50 text-slate-700 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const LearnerSessionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    api.get("/my/sessions").then(r => setSessions(r.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const upcoming = sessions.filter(s => s.date >= today && s.status !== "cancelled" && s.status !== "completed");
  const past = sessions.filter(s => s.date < today || s.status === "completed");
  const display = tab === "upcoming" ? upcoming : past;

  if (loading) return (
    <AdminLayout title="My Sessions" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="My Sessions" variant={variant}>
      <motion.div className="space-y-5" variants={container} initial="hidden" animate="show" data-testid="sessions-page">
        <motion.div variants={item}>
          <h1 className="text-2xl font-extrabold tracking-tight">My ILT Sessions</h1>
          <p className="text-sm text-muted-foreground mt-1">{upcoming.length} upcoming, {past.length} past sessions</p>
        </motion.div>

        <motion.div variants={item}>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="upcoming" className="text-xs gap-1.5" data-testid="tab-upcoming"><Calendar className="w-3.5 h-3.5" /> Upcoming ({upcoming.length})</TabsTrigger>
              <TabsTrigger value="past" className="text-xs gap-1.5" data-testid="tab-past"><CheckCircle2 className="w-3.5 h-3.5" /> Past ({past.length})</TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {display.length === 0 ? (
          <motion.div variants={item}>
            <Card className="border shadow-none"><CardContent className="p-12 text-center text-muted-foreground"><Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No {tab} sessions</p></CardContent></Card>
          </motion.div>
        ) : display.map((s, i) => (
          <motion.div key={s.id} variants={item}>
            <Card className="border shadow-none hover:shadow-md transition-shadow cursor-pointer" data-testid={`session-${i}`} onClick={() => navigate(`/learner/sessions/${s.id}`)}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold">{s.title}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {s.date} &middot; {s.start_time}-{s.end_time}</span>
                      {s.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.location}</span>}
                      {s.instructor && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {s.instructor}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={cn("text-[10px] border", statusColor[s.status] || "")}>
                    {s.status}
                  </Badge>
                  {tab === "past" && !s.has_feedback && (
                    <Button variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={() => navigate(`/tenant/sessions/${s.id}`)} data-testid={`give-feedback-${i}`}>
                      <MessageSquare className="w-3 h-3" /> Give Feedback
                    </Button>
                  )}
                  {tab === "past" && s.has_feedback && (
                    <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Feedback Given</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </AdminLayout>
  );
};

export default LearnerSessionsPage;
